import { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  HStack,
  Heading,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BulkDeleteBar from "../components/BulkDeleteBar";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import Pagination from "../components/Pagination";
import { OriginsApi, type OriginListResponse } from "../api/origins";
import logger from "../analytics/logger";
import { fmtDate } from "../utils/datetime";

export default function Origins() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1);
  const pageSize = Math.max(1, parseInt(params.get("page_size") ?? "40", 10) || 40);

  const [q, setQ] = useState(params.get("q") ?? "");
  const [data, setData] = useState<OriginListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const confirm = useDisclosure();
  const toast = useToast();

  const [prevData, setPrevData] = useState(data);
  if (data !== prevData) {
    setPrevData(data);
    setSelectedIds(new Set());
  }

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await OriginsApi.list({
          page,
          page_size: pageSize,
          q: params.get("q") ?? undefined,
        });
        if (!active) return;
        setData(d);
      } catch (e) {
        if (!active) return;
        logger.error("origins_list_failed", e);
        setError(
          (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
            "Failed to load origins",
        );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [page, pageSize, params, refetchTick]);

  const visibleIds = data?.origins.map((o) => o.id) ?? [];
  const selectedOnPage = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allOnPageSelected = visibleIds.length > 0 && selectedOnPage === visibleIds.length;
  const someOnPageSelected = selectedOnPage > 0 && !allOnPageSelected;

  const toggleAllOnPage = (checked: boolean) =>
    setSelectedIds(() => (checked ? new Set(visibleIds) : new Set()));
  const toggleOne = (id: string, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const doBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setDeleting(true);
    try {
      const res = await OriginsApi.bulkDelete(ids);
      const deleted = res.deleted.length;
      const refused = res.refused.length;
      if (deleted) {
        toast({
          title: `Deleted ${deleted} origin${deleted === 1 ? "" : "s"}`,
          description: refused ? `${refused} skipped (still have events)` : undefined,
          status: refused ? "warning" : "success",
          duration: 5000,
          isClosable: true,
        });
      } else if (refused) {
        toast({
          title: "Nothing deleted",
          description: `${refused} origin${refused === 1 ? "" : "s"} still have events`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
      setSelectedIds(new Set());
      setRefetchTick((t) => t + 1);
      confirm.onClose();
    } catch (e) {
      logger.error("origins_bulk_delete_failed", e);
      toast({
        title: "Delete failed",
        description:
          (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "Unknown error",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  const refresh = () => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    next.set("page", "1");
    setParams(next);
  };

  const reset = () => {
    setQ("");
    setParams(new URLSearchParams({ page: "1" }));
  };

  const setPageSize = (size: number) => {
    const next = new URLSearchParams(params);
    next.set("page_size", String(size));
    next.set("page", "1");
    setParams(next);
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next);
  };

  return (
    <Box p={6}>
      <HStack justify="space-between" align="center" mb={4}>
        <Heading size="lg">Origins</Heading>
        <Button size="sm" colorScheme="blue" onClick={() => navigate("/origins/new")}>
          Add origin
        </Button>
      </HStack>

      <Box borderWidth="1px" borderRadius="md" p={4} mb={4}>
        <HStack spacing={3} wrap="wrap">
          <Input
            size="sm"
            flex="1"
            minW="240px"
            placeholder="Search by slug or display name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") refresh();
            }}
          />
          <Button size="sm" colorScheme="blue" onClick={refresh}>
            Refresh
          </Button>
          <Button size="sm" variant="ghost" onClick={reset}>
            Reset
          </Button>
        </HStack>
      </Box>

      <BulkDeleteBar count={selectedIds.size} onDelete={confirm.onOpen} />
      <ConfirmDeleteDialog
        isOpen={confirm.isOpen}
        onClose={confirm.onClose}
        onConfirm={doBulkDelete}
        count={selectedIds.size}
        entity="origin"
        busy={deleting}
      />

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {loading ? (
        <Box py={10} textAlign="center">
          <Spinner />
        </Box>
      ) : data ? (
        <>
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th width="36px" px={2}>
                    <Checkbox
                      isChecked={allOnPageSelected}
                      isIndeterminate={someOnPageSelected}
                      onChange={(e) => toggleAllOnPage(e.target.checked)}
                      aria-label="Select all on this page"
                    />
                  </Th>
                  <Th>Created</Th>
                  <Th>Display name</Th>
                  <Th>Slug</Th>
                  <Th isNumeric>Events</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.origins.map((o) => (
                  <Tr
                    key={o.id}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    onClick={() => navigate(`/origins/${o.id}`)}
                  >
                    <Td px={2} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        isChecked={selectedIds.has(o.id)}
                        onChange={(e) => toggleOne(o.id, e.target.checked)}
                        aria-label={`Select origin ${o.slug}`}
                      />
                    </Td>
                    <Td whiteSpace="nowrap">{fmtDate(o.created_at)}</Td>
                    <Td>{o.display_name ?? "—"}</Td>
                    <Td>{o.slug}</Td>
                    <Td isNumeric>{o.event_count}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            onChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      ) : null}
    </Box>
  );
}
